// assets/minimum-order.js - Sleek inline message version
(() => {
  const MINIMUM_CENTS = 5000; // €50
  let lastTotal = 0;
  let isRefreshing = false;

  const formatMoney = (cents) =>
    new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR' }).format(cents / 100);

  const messageText = (shortfallCents) =>
    `Minimum afhaalbedrag is €50. Voeg nog ${formatMoney(shortfallCents)} toe om verder te gaan.`;

  const getCheckoutButtons = () => [
    ...document.querySelectorAll('#checkout, #CartDrawer-Checkout, button[name="checkout"]'),
  ];

  const getMessageEls = () => [...document.querySelectorAll('[data-minimum-order-message]')];

  const setUi = (totalCents) => {
    const shortfall = Math.max(0, MINIMUM_CENTS - totalCents);
    const blocked = shortfall > 0;

    getCheckoutButtons().forEach((btn) => {
      if (blocked) {
        // Set attributes
        btn.setAttribute('disabled', 'disabled');
        btn.setAttribute('aria-disabled', 'true');
        btn.dataset.minimumBlocked = '1';
        
        // Apply inline styles to override everything
        btn.style.backgroundColor = '#d9d9d9';
        btn.style.color = '#666';
        btn.style.cursor = 'not-allowed';
        btn.style.opacity = '0.6';
        btn.style.pointerEvents = 'none';
        
        // Add a style tag to override ::after pseudo-element
        if (!document.getElementById('minimum-order-button-override')) {
          const style = document.createElement('style');
          style.id = 'minimum-order-button-override';
          style.textContent = `
            button[data-minimum-blocked="1"]::after,
            #checkout[data-minimum-blocked="1"]::after,
            #CartDrawer-Checkout[data-minimum-blocked="1"]::after {
              box-shadow: none !important;
              outline: none !important;
            }
            button[data-minimum-blocked="1"]:hover::after,
            button[data-minimum-blocked="1"]:focus::after {
              box-shadow: none !important;
            }
          `;
          document.head.appendChild(style);
        }
      } else {
        // Remove disabled state
        btn.removeAttribute('disabled');
        btn.setAttribute('aria-disabled', 'false');
        btn.dataset.minimumBlocked = '0';
        
        // Remove inline styles to let theme CSS take over
        btn.style.backgroundColor = '';
        btn.style.color = '';
        btn.style.cursor = '';
        btn.style.opacity = '';
        btn.style.pointerEvents = '';
      }
    });

    getMessageEls().forEach((el) => {
      if (blocked) {
        // Sleek inline message - just text with red left border
        el.style.display = 'block';
        el.style.margin = '0 0 10px 0';
        el.style.padding = '0 0 0 12px';
        el.style.backgroundColor = 'transparent';
        el.style.borderLeft = '3px solid #fb2f30';
        el.style.borderRadius = '0';
        el.style.fontSize = '13px';
        el.style.lineHeight = '1.5';
        el.style.color = '#666';
        el.textContent = messageText(shortfall);
      } else {
        // Hide message completely
        el.style.display = 'none';
        el.textContent = '';
      }
    });
  };

  const refresh = async () => {
    if (isRefreshing) return;
    isRefreshing = true;

    try {
      const res = await fetch('/cart.js', { 
        credentials: 'same-origin', 
        cache: 'no-store',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      
      if (!res.ok) {
        isRefreshing = false;
        return;
      }
      
      const cart = await res.json();
      const newTotal = cart.total_price || 0;
      
      // Only update UI if total actually changed
      if (newTotal !== lastTotal) {
        lastTotal = newTotal;
        setUi(lastTotal);
      }
    } catch (err) {
      console.warn('Cart refresh failed:', err);
    } finally {
      isRefreshing = false;
    }
  };

  // Debounced refresh - wait 200ms after last change
  let refreshTimer = null;
  const scheduleRefresh = () => {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(refresh, 200);
  };

  // Block form submission when under minimum
  document.addEventListener(
    'submit',
    async (e) => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;

      const action = (form.getAttribute('action') || '').toLowerCase();
      if (!action.includes('/cart')) return;

      await refresh();

      if (lastTotal < MINIMUM_CENTS) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    true
  );

  // Block checkout button clicks when under minimum
  document.addEventListener(
    'click',
    async (e) => {
      const btn = e.target.closest('#checkout, #CartDrawer-Checkout, button[name="checkout"]');
      if (!btn) return;

      await refresh();

      if (lastTotal < MINIMUM_CENTS) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    true
  );

  // Listen for quantity changes
  document.addEventListener('change', (e) => {
    const target = e.target;
    if (target?.matches('input.quantity__input') || 
        target?.closest('quantity-input') ||
        target?.name === 'updates[]') {
      scheduleRefresh();
    }
  });

  // Listen for remove button clicks
  document.addEventListener('click', (e) => {
    if (e.target?.closest('cart-remove-button, [data-cart-remove], .cart-remove-button')) {
      scheduleRefresh();
    }
  });

  // Listen for Dawn's cart update events
  document.addEventListener('cart:refresh', scheduleRefresh);
  document.addEventListener('cart:change', scheduleRefresh);
  document.addEventListener('cart:updated', scheduleRefresh);

  // Also listen for fetch requests to cart (Dawn's AJAX updates)
  const originalFetch = window.fetch;
  window.fetch = function(...args) {
    const url = args[0];
    if (typeof url === 'string' && (url.includes('/cart/') || url.includes('cart.js'))) {
      // Cart was updated via fetch, schedule refresh
      setTimeout(scheduleRefresh, 100);
    }
    return originalFetch.apply(this, args);
  };

  // MutationObserver - watch for DOM changes in cart
  const observeCart = () => {
    const cartDrawer = document.querySelector('#CartDrawer-CartItems');
    const mainCart = document.querySelector('.cart__contents');
    
    if (cartDrawer || mainCart) {
      const observer = new MutationObserver(() => {
        scheduleRefresh();
      });
      
      if (cartDrawer) observer.observe(cartDrawer, { childList: true, subtree: true });
      if (mainCart) observer.observe(mainCart, { childList: true, subtree: true });
    }
  };

  // Initialize
  const init = () => {
    refresh();
    observeCart();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();